import { useRef } from 'react';
import { useFocusTrap } from '../useFocusTrap';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Extra class on the card, for a modal-specific max-width/layout tweak
   * without touching the shared `.overlay`/`.overlay-card` chrome. */
  className?: string;
  testId?: string;
}

/** Small reusable accessible modal: overlay + card, `role="dialog"`
 * `aria-modal="true"` labelled by its own title, focus-trapped (Tab cycles
 * inside; focus returns to the opener on close), Esc closes, click-outside
 * closes, and a visible close (x) button.
 *
 * Reuses the existing `.overlay`/`.overlay-card` classes (same "lit arcade
 * panel over a dimmed scrim" language as PauseOverlay/CountdownOverlay) --
 * it does NOT touch the `.overlay` rule itself (still the same
 * `rgba(11,14,44,0.5)` scrim used by the countdown/pause overlays), and per
 * the brief only ever mounts from the menu/lobby/pause screens, never
 * layered on top of `.game-canvas` mid-gameplay in a way that would
 * perturb a pixel assert -- callers gate that, this component is agnostic
 * to where it's rendered. */
export function Modal({ title, onClose, children, className, testId }: ModalProps): JSX.Element {
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`).current;
  // Pixel-font usage policy: Press Start 2P stays for <=2-word titles
  // ("Settings"); anything longer ("How to Play") reads as a short PHRASE,
  // not a label, and moves to the body sans (`.title-sans`) -- computed
  // from the actual title text so every current/future caller follows the
  // same rule without a per-call-site decision.
  const isShortLabel = title.trim().split(/\s+/).length <= 2;

  useFocusTrap(cardRef, true, onClose);

  function onOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>): void {
    // Click-outside-closes: only when the mousedown target is the scrim
    // itself, never a bubbled event that started inside the card (a drag
    // that starts inside the card and releases over the scrim would still
    // have its mousedown target be a card descendant, so this is safe).
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className="overlay modal-overlay" onMouseDown={onOverlayMouseDown} data-testid={testId ? `${testId}-overlay` : undefined}>
      <div
        className={`overlay-card modal-card${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={cardRef}
        tabIndex={-1}
        data-testid={testId}
      >
        <div className="modal-header">
          <h2 id={titleId} className={isShortLabel ? undefined : 'title-sans'}>
            {title}
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
